import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Tabs,
  Typography,
  Checkbox,
  Table, Empty, Divider
} from 'antd';
import { LeftOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import {
  createClient,
  updateClient,
  getClientById,
  checkClientEmailExists,
  getClientFullHistory,
} from '../../services/participant';
import { listGenders, listClientRoles, listAllStates } from '../../services/catalogs';
import { Space } from 'antd';
import { Tooltip } from 'antd';
import PlayerProfile from './PlayerProfile';
import FanProfile from './FanProfile';
import OwnerProfile from './OwnerProfile';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;

const phoneRegex = /^\d{10}$/;

const ParticipantUpsertPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const navigate = useNavigate();
  const { message } = App.useApp();

  const { canAny } = usePermissions();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [genders, setGenders] = useState([]);
  const [activeTab, setActiveTab] = useState('general');
  const [subscriptions, setSubscriptions] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [statesGeneral, setStatesGeneral] = useState([]);
  const [subRoles, setSubRoles] = useState([]);
  const [showSubRole, setShowSubRole] = useState(false);


  const selectedRoleId = Form.useWatch('roleId', form);
  const selectedSubRoleId = Form.useWatch('subRoleId', form);


  /* ========================
    Cargar catálogos
  ========================= */
  useEffect(() => {
    listClientRoles()
      .then(setRoles)
      .catch(() => message.error('No se pudieron cargar los roles'));
    listGenders()
      .then(setGenders)
      .catch(() => message.error('No se pudo cargar el catálogo de género'));
    listAllStates().then(setStatesGeneral);
  }, [message]);

  /* ========================
    Cargar participante (edición)
  ========================= */
  useEffect(() => {
    if (!isEdit || !id) return;

    setLoading(true);
    getClientById(id)
      .then((client) => {
        const player = client.userRolPlayer;
        const fan = client.userRolAmateur;
        const owner = client.userRolOwner;
        form.setFieldsValue({
          email: client.email,
          firstName: client.firstName,
          lastName: client.lastName,
          secondLastName: client.secondLastName,
          phone: client.phone,
          birthDate: client.birthDate ? dayjs(client.birthDate) : null,
          genderId: client.genderId,
          roleId: client.roleId,
          subRoleId: client.subRoleId,
          // postalCode: client.postalCode,
          stateId: client.stateId,
          isActive: client.isActive,
          acceptTerms: client?.acceptTerms || undefined,
          referralCode: client?.referralCode || undefined,
           playerProfile: player
              ? {
                  stateId: player.stateId,
                  cityId: player.cityId,
                  positionId: player.positionId,
                  fieldTypeId: player.fieldTypeId,
                  playFrequencyId: player.playFrequencyId,
                  purchaseFrequencyId: player.purchaseFrequencyId,
                  jerseySizeId: player.jerseySizeId,
                  teamName: player.teamName,
                  heightCm: player.heightCm,
                  dominantFootId: player.dominantFootId,
                  cleatsSize: Number(player.cleatsSize), // InputNumber
                  shoeSize: Number(player.shoeSize),     // InputNumber
                  interests: player.interests?.map(i => i.id),
                }
              : undefined,
          fanProfile: fan
            ? {
                stateId: fan.stateId,
                cityId: fan.cityId,
                positionId: fan.positionId,
                supportReasonId: fan.supportReasonId,
                teamId: fan.teamId,
                interests: fan.interests?.map(i => i.id),
                purchaseFrequencyId: fan.purchaseFrequencyId,
                jerseySizeId: fan.jerseySizeId,
                shoeSize: Number(fan.shoeSize),
              }
            : undefined,
          ownerProfile: owner
            ? {
                roleId: owner?.roleId,
                teamSizeId: owner?.teamSizeId,
                locationId: owner?.locationId,
                leagueName: owner?.leagueName,
                categoryEquipmentId: owner?.categoryEquipmentId,
                templateSizeId: owner?.templateSizeId,
                manageUniforms: owner?.manageUniforms,
                interests: owner.interests?.map(i => i.id),
                purchaseFrequencyId: player.purchaseFrequencyId,
                jerseySizeId: player.jerseySizeId,
                shoeSize: Number(player.shoeSize),
              }
            : undefined,
        });

      })
      .catch(() => message.error('No se pudo cargar el participante'))
      .finally(() => setLoading(false));
  }, [id, form, message, isEdit]);

  /* ========================
    Cargar Historial
  ========================= */
  const reloadHistory = useCallback(async () => {
    if (!id) return;

    try {
      setHistoryLoading(true);

      const data = await getClientFullHistory(id);

      setSubscriptions(data.subscriptions || []);
      setTokens(data.tokens || []);
      setCompetitions(data.competitions || []);
    } catch {
      message.error('No se pudo cargar el historial del participante');
    } finally {
      setHistoryLoading(false);
    }
  }, [id, message]);


  useEffect(() => {
    if (activeTab !== 'history' || !id) return;
    reloadHistory();
  }, [activeTab, id, reloadHistory]);


  /* ========================
    Validación + submit
  ========================= */
  const getRoleById = (roleId) =>
    roles.find(r => r.id === roleId);


  const buildRoleProfilePayload = (values) => {
    const role = getRoleById(values.roleId);
    const clean = (obj) => {
      // Elimina keys que son undefined o null
      if (!obj) return undefined;
      const filtered = Object.fromEntries(
        Object.entries(obj).filter(([_, v]) => v !== undefined && v !== null)
      );
      return Object.keys(filtered).length ? filtered : undefined;
    };
    if (!role) return {};

    switch (role.code) {
      case 'JUGADOR':
        return clean(values.playerProfile)
          ?
            {
              userRolPlayer: {
                ...values.playerProfile,
                cleatsSize:
                  values.playerProfile.cleatsSize != null
                    ? String(values.playerProfile.cleatsSize)
                    : null,
                shoeSize:
                  values.playerProfile.shoeSize != null
                    ? String(values.playerProfile.shoeSize)
                    : null,
              },
            }
          : {} ;
      case 'AFICIONADO_PORRA':
        return clean(values.fanProfile)
          ?
            {
              userRolAmateur: {
                ...values.fanProfile,
              },
            }
          : {};
      case 'DT_ORGANIZADOR':
        return clean(values.ownerProfile)
          ?
            {
              userRolOwner: {
                ...values.ownerProfile,
              },
            }
          : {} ;
      default:
        return {};
    }
  };


  const onSubmit = async (values) => {
    try {
      setLoading(true);

      const exists = await checkClientEmailExists(values.email.trim(), id ? Number(id) : id);
      if (exists === true) {
        message.warning('Ya existe un participante con este correo');
        return;
      }

      const roleProfilePayload = buildRoleProfilePayload(values);

      const payload = {
        email: values.email.trim(),
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        secondLastName: values.secondLastName || null,
        phone: values.phone || null,
        birthDate: values.birthDate?.toISOString() || null,
        genderId: values.genderId || null,
        roleId: values.roleId,
        subRoleId: values.subRoleId || null,
        // postalCode: Number(values.postalCode) || null,
        stateId: values.stateId || null,
        isActive: values.isActive,
        acceptTerms: values.acceptTerms,
        ...roleProfilePayload,
      };


      if (isEdit) {
        await updateClient(id, payload);
        message.success('Participante actualizado correctamente');
      } else {
        await createClient(payload);
        message.success('Participante creado correctamente');
      }

      navigate('/participants/all');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        form.setFields([
          { name: 'email', errors: ['El correo ya está registrado'] },
        ]);
      } else {
        message.error('Error al guardar el participante');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedRoleId || !roles.length) {
      setSubRoles([]);
      setShowSubRole(false);
      return;
    }

    const role = roles.find(r => r.id === selectedRoleId);

    if (role?.subRoles?.length) {
      setSubRoles(role.subRoles);
      setShowSubRole(true);
    } else {
      setSubRoles([]);
      setShowSubRole(false);
      form.setFieldsValue({ subRoleId: undefined });
    }
  }, [selectedRoleId, roles, form]);


  /* ========================
    Columnas para tablas historial
  ========================= */
  const subscriptionColumns = [
    {
      title: 'Membresía',
      dataIndex: ['plan', 'name'],
    },
    {
      title: 'Fecha',
      dataIndex: 'startDate',
      render: (v) => dayjs(v).format('DD/MM/YYYY'),
    },
    {
      title: 'Pago',
      dataIndex: 'total',
      render: (v) => `$${Number(v).toFixed(2)}`,
    },
    {
      title: 'Estatus',
      dataIndex: 'status',
      render: (v) => (v === 'ACTIVE' ? 'Activo' : 'Vencido'),
    },
  ];
  const tokenColumns = [
    {
      title: 'Cantidad',
      dataIndex: 'quantity',
    },
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      render: (v) => dayjs(v).format('DD/MM/YYYY'),
    },
    {
      title: 'Pago',
      dataIndex: 'payment',
      render: (v) =>
        v != null ? `$${Number(v).toFixed(2)}` : '-',
    },
  ];

  const competitionColumns = [
    {
      title: 'Competencia',
      dataIndex: ['competition', 'description'],
    },
    {
      title: 'Modalidad',
      dataIndex: ['competition', 'modality', 'name'],
    },
    {
      title: 'Género',
      dataIndex: ['competition', 'gender', 'name'],
    },
    {
      title: 'Fecha competencia',
      dataIndex: ['competition', 'startDate'],
      render: (v) => dayjs(v).format('DD/MM/YYYY'),
    },
    {
      title: 'Resultado',
      dataIndex: 'result',
      render: (_, r) => ((_ || '-') + (r.position ? ` (${r.position}°)` : ''))
    },
    {
      title: 'Puntos',
      dataIndex: 'totalScore',
    },
  ];

  /* ========================
    Perfiles
  ========================= */
  const ProfileByRole = ({ roleId, subRoleId, roles }) => {
    const roleProfile = useMemo(() => {
      if (!roleId || !roles?.length) return null;
      return roles.find(r => r.id === roleId);
    }, [roleId, roles]);

    if (!roleId) {
      return (
        <Empty description="Es necesario elegir el rol del participante para mostrar esta sección" />
      );
    }

    if (!roleProfile) {
      return <Empty description="Cargando perfil..." />;
    }

    if (roleProfile.code === 'JUGADOR') {
      return <PlayerProfile />;
    }

    if (roleProfile.code === 'AFICIONADO_PORRA') {
      return <FanProfile />;
    }

    if (roleProfile.code === 'DT_ORGANIZADOR') {
      const subRole = roleProfile?.subRoles?.find(r => r.id === subRoleId)
      return <OwnerProfile role={subRole}/>;
    }

    return <Empty description="Perfil no disponible para este rol" />;
  };



  /* ========================
    Tabs
  ========================= */
  const tabs = [
    {
      key: 'general',
      label: 'Información General',
      children: (
        <>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Nombre(s)"
                name="firstName"
                rules={[{ required: true, message: 'Campo obligatorio' }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label="Apellido paterno"
                name="lastName"
                rules={[{ required: true, message: 'Campo obligatorio' }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                label="Apellido materno"
                name="secondLastName"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Correo electrónico"
                name="email"
                rules={[
                  { required: true, message: 'Campo obligatorio' },
                  { type: 'email', message: 'Correo inválido' },
                ]}
              >
                <Input disabled={isEdit} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Teléfono móvil"
                name="phone"
                rules={[
                  { pattern: phoneRegex, message: 'Debe tener 10 dígitos' },
                ]}
              >
                <Input
                  maxLength={10}
                  onChange={(e) =>
                    form.setFieldsValue({
                      phone: e.target.value.replace(/\D/g, ''),
                    })
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={3}>
              <Form.Item
                label="Estatus"
                name="status"
                valuePropName="checked"
              >
                <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
              </Form.Item>
            </Col>
            <Col xs={24} md={3}>
              <Form.Item label="Estado" name="stateId"
              rules={[{ required: true, message: 'Campo obligatorio' }]}>
                <Select
                    placeholder="Selecciona estado"
                    options={statesGeneral.map(s => ({
                      key: s.id,
                      value: s.id,
                      label: s.description,
                    }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label="Fecha de nacimiento"
                name="birthDate"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      return dayjs().diff(value, 'year') >= 18
                        ? Promise.resolve()
                        : Promise.reject(
                          new Error('Debe ser mayor de 18 años'),
                        );
                    },
                  },
                ]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Género" name="genderId">
                <Select
                  placeholder="Selecciona un género"
                  options={genders.map((g) => ({
                    value: g.id,
                    label: g.description,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label="Rol"
                name="roleId"
                rules={[{ required: true, message: 'Campo obligatorio' }]}
              >
                <Select
                  options={roles.map((r) => ({
                    value: r.id,
                    label: r.name,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Código para referir" name="referralCode">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} style={{ marginTop: 24 }}>
              <Form.Item
                name="acceptTerms"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(new Error('Debes aceptar los términos')),
                  },
                ]}
              >
                <Checkbox>Acepto términos y condiciones</Checkbox>
              </Form.Item>
            </Col>
            {showSubRole && (<Col xs={24} md={6}>
                {/* ========================
                    Sub-Rol (OBLIGATORIO)
                ========================= */}
                <Form.Item
                  label="Subrol"
                  name={'subRoleId'}
                  rules={[{ required: true, message: 'Campo obligatorio' }]}
                >
                  <Select
                    placeholder="Selecciona tu rol"
                    options={subRoles.map(r => ({
                      value: r.id,
                      label: r.name,
                    }))}
                  />
                </Form.Item>
            </Col>)}
          </Row>

        </>
      ),
    },
    {
      key: 'profile',
      label: 'Perfil',
      disabled: !isEdit,
      children: (
        <ProfileByRole
          roleId={selectedRoleId}
          subRoleId={selectedSubRoleId}
          roles={roles}
          key={selectedRoleId}
        />
      ),
    },
    {
      key: 'history',
      label: 'Historial',
      disabled: !isEdit,
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Row justify="end" style={{ marginBottom: 8 }}>
            <Tooltip title="Recargar historial">
              <Button
                icon={<ReloadOutlined />}
                onClick={reloadHistory}
                loading={historyLoading}
              />
            </Tooltip>
          </Row>
          <Card loading={historyLoading}>
            <Row gutter={[16, 16]}>
              {/* Membresías */}
              <Col xs={24} md={12}>
                <Title level={5}>Membresía</Title>
                <Table
                  rowKey="id"
                  columns={subscriptionColumns}
                  dataSource={subscriptions}
                  pagination={{ pageSize: 5, }}
                  scroll={{ x: 'max-content' }}
                  locale={{ emptyText: <Empty description="Sin registros" /> }}
                />
              </Col>

              {/* Tokens */}
              <Col xs={24} md={12}>
                <Title level={5}>Tokens</Title>
                <Table
                  rowKey="id"
                  columns={tokenColumns}
                  dataSource={tokens}
                  scroll={{ x: 'max-content' }}
                  pagination={{ pageSize: 5, }}
                  locale={{ emptyText: <Empty description="Sin registros" /> }}
                />
              </Col>
            </Row>

            <Divider />

            {/* Competencias */}
            <Title level={5}>Competencias</Title>
            <Table
              rowKey="id"
              columns={competitionColumns}
              dataSource={competitions}
              pagination={{ pageSize: 10, }}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: <Empty description="Sin registros" /> }}
            />
          </Card>
        </Space>
      ),
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={() => navigate('/participants/all')}
        />
        <Title level={3}>
          {isEdit ? 'Editar participante' : 'Agregar participante'}
        </Title>
      </div>

      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{ status: true }}
        >
          <Form.Item name="playerProfile" noStyle hidden>
            <Input />
          </Form.Item>
          <Form.Item name="fanProfile" noStyle hidden>
            <Input />
          </Form.Item>
          <Form.Item name="ownerProfile" noStyle hidden>
            <Input />
          </Form.Item>
          <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabs} />

          <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
            <Col>
              <Button onClick={() => navigate('/participants/all')}>
                Cancelar
              </Button>
            </Col>
            {canAny(['participants.create', 'participants.update']) && (<Col>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                Guardar
              </Button>
            </Col>)}
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default ParticipantUpsertPage;
